import { ReactNode, useEffect, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { IAPICodeDTO, IContinuousRoute, ISelectOption } from '$/types'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  useGetQRAAssessmentQuery,
  usePatchQRAAssessmentMutation
} from 'app/services/api/qra/assessments'
import { useGetQRASavedStateQuery } from 'app/services/api/qra/savedstates'

import { RiskManagementViewWithRef } from 'features/RiskManagement/Assessments/Scenarios/RiskManagementView'

import { IStateAssessment } from '..'
import { WISAssessmentStatusCode } from '../WISAssessmentStatusCode'
import { WISWrapperType } from './types'
import { useOnEditValidator } from './utils/useOnEditWISValidator'
import { selectScenario } from './whatIfScenarioSelectors'
import { setSelectedScenarioSync } from './whatIfScenarioSlice'
import WISModal from './WISModal'

const EditWISWrapper = ({
  assessmentId,
  stateId,
  scenarioOptions,
  setViewMode,
  isModalOpen,
  closeModal,
  wisName,
  setWisName
}: WISWrapperType) => {
  const dispatch = useAppDispatch()

  const {
    currentData: currentAssessment,
    isLoading: isCurrentAssessmentLoading
  } = useGetQRAAssessmentQuery(assessmentId ?? skipToken)

  useEffect(() => {
    currentAssessment?.name && setWisName(currentAssessment.name)
  }, [currentAssessment?.name, setWisName])

  const [wasScenarioSelected, setWasScenarioSelected] = useState<boolean>(false)
  const [isScenarioNameEmpty, setIsScenarioNameEmpty] = useState<boolean>(false)
  const [assessmentRan, setAssessmentRan] = useState<
    IStateAssessment | undefined
  >()
  const [previouslyRanScenario, setPreviouslyRanScenario] =
    useState<ISelectOption | null>(null)

  const { currentData: savedState } = useGetQRASavedStateQuery(
    stateId ?? skipToken
  )

  const [rerunAssessment, { isLoading: rerunAssessmentLoading }] =
    usePatchQRAAssessmentMutation()

  const scenario = useAppSelector(selectScenario)

  useEffect(() => {
    const foundScenario = scenarioOptions.find(
      (scenario) =>
        scenario.label === currentAssessment?.assessmentScenarios[0]?.name
    )

    if (foundScenario) {
      setPreviouslyRanScenario(foundScenario)
    }
  }, [currentAssessment?.assessmentScenarios, dispatch, scenarioOptions])

  const rerunWIS = () => {
    let selectedRoutes: IContinuousRoute[] = []
    let rerunRiskLevels: IAPICodeDTO[] = []
    let rerunClassLocations: IAPICodeDTO[] = []
    let rerunConsequenceLevels: IAPICodeDTO[] = []

    selectedRoutes =
      currentAssessment?.assessmentContinuousRoutes.map((lines) => ({
        ...lines,
        id: 0
      })) ?? []

    rerunRiskLevels =
      currentAssessment?.assessmentRiskLevels.map((lines) => ({
        ...lines,
        id: 0
      })) ?? []

    rerunClassLocations =
      currentAssessment?.assessmentClassLocations.map((lines) => ({
        ...lines,
        id: 0
      })) ?? []

    rerunConsequenceLevels =
      currentAssessment?.assessmentConsequenceLevels.map((lines) => ({
        ...lines,
        id: 0
      })) ?? []

    const stagingAssessment: Partial<IStateAssessment> = {
      savedStateId: stateId,
      id: currentAssessment?.id,
      name: wisName,
      statusCode: WISAssessmentStatusCode.New,
      typeCode: 'WIS',
      assessmentContinuousRoutes: selectedRoutes,
      assessmentRiskLevels: rerunRiskLevels ?? [],
      assessmentClassLocations: rerunClassLocations ?? [],
      assessmentConsequenceLevels: rerunConsequenceLevels ?? [],
      assessmentRiskModels: [{ riskModelId: '1' }],
      assessmentScenarios: [
        {
          id: 0,
          name: scenario.name,
          rules: scenario.rules
        }
      ]
    }

    const wisAssessmentPromise: Promise<IStateAssessment> =
      rerunAssessment(stagingAssessment).unwrap()

    wisAssessmentPromise.then((assessment) => setAssessmentRan(assessment))
  }

  const existingAssessmentNames =
    savedState?.assessments.map(
      (assessment: IStateAssessment) => assessment.name
    ) || []

  const [isScenarioNameValid, scenarioNameValidationMessage] =
    useOnEditValidator(
      wisName,
      existingAssessmentNames,
      currentAssessment?.name ?? '',
      isScenarioNameEmpty
    )

  // Add this logic to disable validation during loading
  const shouldShowValidation =
    !rerunAssessmentLoading && !isCurrentAssessmentLoading && !assessmentRan

  const renderContent: ReactNode = (
    <RiskManagementViewWithRef
      wisName={wisName}
      scenarioOptions={scenarioOptions}
      setWisName={setWisName}
      currentAssessment={currentAssessment}
      title='Edit What-If Scenario'
      setViewMode={setViewMode}
      setWasScenarioSelected={setWasScenarioSelected}
      setIsScenarioNameEmpty={setIsScenarioNameEmpty}
      scenarioNameValidationMessage={
        shouldShowValidation ? scenarioNameValidationMessage : ''
      }
      isScenarioNameValid={shouldShowValidation ? isScenarioNameValid : true}
      previouslyRanScenario={previouslyRanScenario}
    />
  )

  const canConfirm =
    wasScenarioSelected && isScenarioNameValid && wisName.length > 0

  const handleClose = (pathnameOverride?: string) => {
    setWisName('')
    dispatch(setSelectedScenarioSync({ id: 0, name: '', rules: [] }))
    closeModal(pathnameOverride)
  }

  return (
    <WISModal
      onConfirm={rerunWIS}
      canConfirm={canConfirm}
      renderContent={renderContent}
      isModalOpen={isModalOpen}
      closeModal={handleClose}
      assessmentRan={assessmentRan}
      footerApplyLabel='Rerun Scenario'
      onCancel={handleClose}
      isLoading={
        rerunAssessmentLoading ||
        isCurrentAssessmentLoading ||
        assessmentRan != null
      }
      showRunConfirm
    />
  )
}

export default EditWISWrapper
